/**
 * Central enums shared by web and api.
 * MUST stay in sync with prisma/schema.prisma.
 *
 * Keeping these as `as const` arrays of literals (rather than TS enums)
 * gives us cheap runtime arrays for Zod and forms while keeping the
 * type system strict.
 */

export const BROKERS = ['IBKR', 'HL', 'OTHER'] as const;
export type Broker = (typeof BROKERS)[number];

export const ACCOUNT_TYPES = ['ACTIVE_TRADING', 'LONG_TERM_INVEST'] as const;
export type AccountType = (typeof ACCOUNT_TYPES)[number];

export const PORTFOLIO_MANDATES = ['SWING_TRADING', 'LONG_TERM_INVESTING'] as const;
export type PortfolioMandate = (typeof PORTFOLIO_MANDATES)[number];

export const INVESTMENT_THESIS_STATUSES = [
  'DRAFT',
  'ACTIVE',
  'UNDER_REVIEW',
  'BROKEN',
  'CLOSED',
] as const;
export type InvestmentThesisStatus = (typeof INVESTMENT_THESIS_STATUSES)[number];

export const INVESTMENT_PORTFOLIO_ROLES = [
  'CORE_COMPOUNDER',
  'GROWTH',
  'CYCLICAL',
  'VALUE',
  'DEFENSIVE',
  'SPECULATIVE',
  'OTHER',
] as const;
export type InvestmentPortfolioRole = (typeof INVESTMENT_PORTFOLIO_ROLES)[number];

export const INVESTMENT_THESIS_IDENTITY_SOURCES = ['HL_CODE', 'MANUAL'] as const;
export type InvestmentThesisIdentitySource = (typeof INVESTMENT_THESIS_IDENTITY_SOURCES)[number];

export const INVESTMENT_REVIEW_CADENCES = ['MONTHLY', 'QUARTERLY', 'SEMIANNUAL', 'ANNUAL'] as const;
export type InvestmentReviewCadence = (typeof INVESTMENT_REVIEW_CADENCES)[number];

export const INVESTMENT_ALLOCATION_STATUSES = [
  'BELOW_TARGET',
  'NEAR_TARGET',
  'ABOVE_TARGET',
  'ABOVE_MAXIMUM',
] as const;
export type InvestmentAllocationStatus = (typeof INVESTMENT_ALLOCATION_STATUSES)[number];

export const SIDES = ['BUY', 'SELL'] as const;
export type Side = (typeof SIDES)[number];

export const ASSET_CLASSES = ['STK', 'ETF', 'OPT', 'FUT', 'CRYPTO', 'CASH', 'OTHER'] as const;
export type AssetClass = (typeof ASSET_CLASSES)[number];

export const TRADE_TYPES = ['SCALP', 'SWING', 'INVEST'] as const;
export type TradeType = (typeof TRADE_TYPES)[number];

export const TRADE_PLAN_STATUSES = [
  'DRAFT',
  'APPROVED',
  'WARN',
  'BLOCKED',
  'EXECUTED',
  'EXPIRED',
  'CANCELLED',
] as const;
export type TradePlanStatus = (typeof TRADE_PLAN_STATUSES)[number];

export const SEVERITIES = ['INFO', 'WARN', 'BLOCK'] as const;
export type Severity = (typeof SEVERITIES)[number];

export const GATE_DECISIONS = ['APPROVED', 'WARN', 'BLOCKED'] as const;
export type GateDecisionKind = (typeof GATE_DECISIONS)[number];

export const RULE_KEYS = [
  'STOP_REQUIRED',
  'MAX_POSITION_PCT_NAV',
  'MAX_DAILY_BUY_PCT_NAV',
  'MAX_RISK_PER_TRADE_PCT_NAV',
  'MIN_REWARD_RISK',
  'MAX_THEME_PCT_NAV',
  'MAX_PORTFOLIO_HEAT_PCT_NAV',
  'MAX_DAILY_LOSS_PCT_NAV',
  'MAX_WEEKLY_LOSS_PCT_NAV',
  'MAX_DRAWDOWN_PCT_NAV',
  'NO_MARGIN_FOR_STOCKS',
  'MAX_ACTIVE_SWINGS',
  'COOLING_OFF_AFTER_STOPOUT_MIN',
  'COOLING_OFF_AFTER_2PCT_DAY_HOURS',
  'NO_PREMARKET_ENTRY',
  'MAX_GROSS_LEVERAGE',
  'NO_SHORT_POSITIONS',
  'NO_REBUY_HIGHER_SAME_DAY',
  'NO_AVERAGE_DOWN_LEVERAGED',
  'MAX_ADD_DAYS_PER_POSITION',
  'NO_SIZE_ESCALATION',
  'REVIEW_PENDING',
  'MAX_STOP_DISTANCE_PCT',
  'MARKET_REGIME_ENTRY',
  'MAX_SIZER_QUANTITY',
  'SYMBOL_COOLING_WINDOW_DAYS',
  'DIRECTION_FLIP_HOURS',
  'REBUILD_PORTFOLIO_RISK_PCT',
  'REBUILD_POSITION_COUNT',
  'RISK_TIER_NEAR_LIMIT',
  'THESIS_MIN_WORDS',
] as const;
export type RuleKey = (typeof RULE_KEYS)[number];

export const BEHAVIOURAL_FLAG_KINDS = [
  'REVENGE_TRADE',
  'REBUY_HIGHER',
  'LOSS_CHASE_ADD',
  'SIZE_ESCALATION',
  'DRAWDOWN_SIZE_UP',
  'BIG_MOVE_CHASE',
  'PLAN_DRIFT',
  'STOP_MIGRATION',
  'SWING_TO_INVEST',
  'CLUSTER_TRADE',
  'MARGIN_USED',
  'STALE_NAV',
  'NO_STOP_ON_OPEN',
] as const;
export type BehaviouralFlagKind = (typeof BEHAVIOURAL_FLAG_KINDS)[number];

export const BEHAVIOURAL_FLAG_STATUSES = ['ACTIVE', 'ACKNOWLEDGED', 'SUPERSEDED'] as const;
export type BehaviouralFlagStatus = (typeof BEHAVIOURAL_FLAG_STATUSES)[number];

export const DISCIPLINE_EVENT_TYPES = [
  'TRADE_WITHOUT_PLAN',
  'POSITION_OVERSIZED',
  'ENTRY_OUTSIDE_PLAN',
  'POSITION_RISK_EXCEEDED',
  'MISSING_STOP',
  'STOP_WIDENED',
] as const;
export type DisciplineEventType = (typeof DISCIPLINE_EVENT_TYPES)[number];

export const JOURNAL_ENTRY_TYPES = ['PRE_TRADE', 'MID_TRADE', 'POST_TRADE', 'REVIEW'] as const;
export type JournalEntryType = (typeof JOURNAL_ENTRY_TYPES)[number];

export const NOTIFICATION_KINDS = [
  'BREACH',
  'JOURNAL_REQUIRED',
  'REVIEW_PENDING',
  'WEEKLY_REVIEW',
  'PRICE_ALERT',
  'IMPORT_DONE',
  'RULE_CHANGE',
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export const CASH_FLOW_KINDS = ['DEPOSIT', 'WITHDRAWAL', 'DIVIDEND', 'FEE'] as const;
export type CashFlowKind = (typeof CASH_FLOW_KINDS)[number];
